// Data hook for FulfillmentIntakePanel. Loads forms once and
// re-partitions them locally by status into needs-filling /
// needs-review / needs-work sub-sections. Also exposes the active
// template list so the templates sub-tab can render without a
// second fetch.

import { useCallback, useEffect, useState } from "react";
import {
  listFulfillmentIntakeForms,
  listFulfillmentIntakeTemplates,
  type FulfillmentIntakeForm,
  type FulfillmentIntakeTemplate,
} from "@/lib/fulfillment/fulfillmentApi";

export type IntakeBuckets = {
  needsFilling: FulfillmentIntakeForm[];
  needsReview: FulfillmentIntakeForm[];
  needsWork: FulfillmentIntakeForm[];
  approved: FulfillmentIntakeForm[];
};

function partition(forms: FulfillmentIntakeForm[]): IntakeBuckets {
  const out: IntakeBuckets = {
    needsFilling: [],
    needsReview: [],
    needsWork: [],
    approved: [],
  };
  for (const f of forms) {
    if (f.status === "pending") out.needsFilling.push(f);
    else if (f.status === "awaiting_review") out.needsReview.push(f);
    else if (f.status === "work_in_progress") out.needsWork.push(f);
    else if (f.status === "approved") out.approved.push(f);
  }
  return out;
}

export type FulfillmentIntakeModel = {
  forms: FulfillmentIntakeForm[];
  buckets: IntakeBuckets;
  templates: FulfillmentIntakeTemplate[];
  busy: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

export function useFulfillmentIntake(): FulfillmentIntakeModel {
  const [forms, setForms] = useState<FulfillmentIntakeForm[]>([]);
  const [templates, setTemplates] = useState<FulfillmentIntakeTemplate[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setBusy(true);
    setError(null);
    const [formsR, templatesR] = await Promise.all([
      listFulfillmentIntakeForms(),
      listFulfillmentIntakeTemplates(),
    ]);
    if (!formsR.ok) {
      setError(formsR.error);
      setBusy(false);
      return;
    }
    if (!templatesR.ok) {
      setError(templatesR.error);
      setBusy(false);
      return;
    }
    setForms(formsR.data.forms);
    setTemplates(templatesR.data.templates);
    setBusy(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    forms,
    buckets: partition(forms),
    templates,
    busy,
    error,
    refresh,
  };
}
